import {
  Box,
  Card,
  CardContent,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { CustomTabPanel } from "pages/Requirements/portal/styles";
import { FC, useMemo, useState } from "react";
import { Link as LinkBase } from "react-router-dom";
import { Requirement } from "types/requirement";
import { BaseCarousel } from "components/Carousel";
import { EPACompetencyRequirementsItem } from "./EPACompetencyRequirementsItem";
import { FeedbackDisplayModal } from "components/Feedback";
import { FeedbackType } from "types/feedback";

function buildRatingsByRequirementFromFeedbacks(
  feedbacks: any[],
): Record<string, { date: string; learnerRating?: number | null; coachRating?: number | null }[]> {
  const ratingsByRequirement: Record<
    string,
    { date: string; learnerRating?: number | null; coachRating?: number | null }[]
  > = {};
  const sorted = [...(feedbacks || [])].sort((a, b) => {
    const da = a.observation_date || a.created_at || "";
    const db = b.observation_date || b.created_at || "";
    return da.localeCompare(db);
  });
  sorted.forEach((fb) => {
    const date = (fb.observation_date || fb.created_at) as string;
    (fb.feedback_skills || []).forEach((skill: any) => {
      const reqId = skill.requirement?.requirement_id ?? skill.requirement_id;
      if (!reqId) return;
      if (!ratingsByRequirement[reqId]) ratingsByRequirement[reqId] = [];
      ratingsByRequirement[reqId].push({
        date,
        learnerRating: skill.self_rating ?? null,
        coachRating: skill.coach_rating ?? null,
      });
    });
  });
  return ratingsByRequirement;
}

type EPACompetencyRequirementsProps = {
  data: any;
  userRole: string;
};

export const EPACompetencyRequirements: FC<EPACompetencyRequirementsProps> = ({
  data,
  userRole,
}) => {
  const [value, setValue] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedStatusLabel, setSelectedStatusLabel] = useState<string>("ALL");
  const [selectedFeedbacks, setSelectedFeedbacks] = useState<any[]>([]);
  const competencyRequirements = useMemo(() => {
    return (data?.competencyRequirements as Requirement[]) || [];
  }, [data]);
  const epaRequirements = useMemo(() => {
    return (data?.epaRequirements as Requirement[]) || [];
  }, [data]);
  const iterRequirements = useMemo(() => {
    return (data?.iterRequirements as Requirement[]) || [];
  }, [data]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const getCurrentSkillLevel = (requirementId: string) => {
    return data?.feedbacksApproved[requirementId] || -1;
  };

  const onClickDot = (requirement: any) => {
    const list =
      data?.feedbacksByRequirementId?.[requirement?.requirement_id];
    const feedbacks = Array.isArray(list)
      ? list.filter((d: FeedbackType) => d.status === "DONE")
      : [];
    setSelectedFeedbacks(feedbacks);
    setSelectedStatusLabel("DONE");
    setOpen(true);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" gutterBottom>
              Requirements
            </Typography>
            <Link
              sx={{ textDecoration: "none" }}
              component={LinkBase}
              to={"/requirements"}
            >
              VIEW ALL
            </Link>
          </Box>

          <Box sx={{}}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="Requirement portal tabs"
            >
              <Tab
                label="EPA"
                id="requirement-tab-0"
                aria-controls="simple-tabpanel-0"
              />
              <Tab
                label="Competency"
                id="requirement-tab-1"
                aria-controls="simple-tabpanel-1"
              />
              <Tab
                label="ITER"
                id="requirement-tab-2"
                aria-controls="simple-tabpanel-2"
              />
            </Tabs>
            <CustomTabPanel value={value} index={0} style={{ padding: 0 }}>
              <Stack spacing={2} direction="row">
                <BaseCarousel
                  data={epaRequirements}
                  itemRender={(epa) => (
                    <EPACompetencyRequirementsItem
                      item={epa}
                      userRole={userRole}
                      skillLevel={getCurrentSkillLevel(epa.requirement_id)}
                      feedbackStats={
                        data?.feedbackStatsByRequirementId[epa.requirement_id]
                      }
                      onClickDot={onClickDot}
                    />
                  )}
                  id="requirement_id"
                />
              </Stack>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <Stack spacing={2} direction="row" sx={{ alignItems: "stretch" }}>
                <BaseCarousel
                  data={competencyRequirements}
                  itemRender={(competency) => (
                    <EPACompetencyRequirementsItem
                      item={competency}
                      userRole={userRole}
                      skillLevel={getCurrentSkillLevel(
                        competency.requirement_id,
                      )}
                      feedbackStats={
                        data?.feedbackStatsByRequirementId[
                          competency.requirement_id
                        ]
                      }
                      onClickDot={onClickDot}
                    />
                  )}
                  id="requirement_id"
                />
              </Stack>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2} style={{ padding: 0 }}>
              <Stack spacing={2} direction="row" sx={{ alignItems: "stretch" }}>
                <BaseCarousel
                  data={iterRequirements}
                  itemRender={(iter) => {
                    const iterFeedbacks =
                      data?.feedbacksByRequirementId?.[iter.requirement_id] ??
                      [];
                    const iterSubRequirements =
                      (iter as any).competencies?.map(
                        (c: any) => c.competency ?? c,
                      ).filter(Boolean) ?? [];
                    const iterRatingsByRequirement =
                      buildRatingsByRequirementFromFeedbacks(iterFeedbacks);
                    return (
                      <EPACompetencyRequirementsItem
                        item={iter}
                        userRole={userRole}
                        skillLevel={getCurrentSkillLevel(iter.requirement_id)}
                        feedbackStats={
                          data?.feedbackStatsByRequirementId[
                            iter.requirement_id
                          ]
                        }
                        onClickDot={onClickDot}
                        isIter
                        iterSubRequirements={iterSubRequirements}
                        iterRatingsByRequirement={iterRatingsByRequirement}
                      />
                    );
                  }}
                  id="requirement_id"
                />
              </Stack>
            </CustomTabPanel>
          </Box>
        </CardContent>
      </Card>
      {open && (
        <FeedbackDisplayModal
          open={open}
          onClose={() => setOpen(false)}
          selectedStatusLabel={selectedStatusLabel}
          filteredData={selectedFeedbacks}
          displayStudentName={false}
        />
      )}
    </>
  );
};
